import type { ColumnDef } from "@tanstack/react-table";

import { useNavigate } from "react-router-dom";
import { ArrowUpDown, X } from "lucide-react";
import { useQueryState } from "nuqs";

import { DataTableCenteredCell } from "@/shared/ui/DataTable";
import type { Worker } from "@/entities/shared/model/workers";
import { Button } from "@/shared/ui/button.tsx";
import { formatDate, numberWithSpaces } from "@/shared/lib/utils";
import { SortButton } from "@/features/SortButton/SortButton";
import { getUSDTBalance } from "@/entities/balance/utils/get-balance";
import { UnnasignedTgTooltip } from "@/features/UnnasignedTgTooltip";
import { SendUserDataButton } from "@/features/SendUserDataButton";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { HandleSelectDPUserButton } from "@/pages/restricted/shared/main-page/ui/head/HeadDashboardProDataTable";
import { Roles } from "@/entities/user";
import { useIsCurrentlyAtDashboardPro } from "@/shared/hooks";

export const WorkersColumns: ColumnDef<Worker>[] = [
  {
    id: "sendUserData",
    header: () => {
      const isMobile = useIsMobile();
      return isMobile ? "Отправить данные" : null;
    },
    cell: ({ row }) => {
      const { _id } = row.original;

      return <SendUserDataButton _id={_id} />;
    },
  },
  {
    accessorKey: "login",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        Логин
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => {
      const isCurrentlyAtDashboardPro = useIsCurrentlyAtDashboardPro([
        Roles.Head,
        Roles.Mentor,
      ]);

      const navigate = useNavigate();
      const handleNavigate = () => {
        navigate(`/head/access?search=${row.getValue("login")}`);
      };

      return (
        <DataTableCenteredCell>
          <div className="relative">
            {isCurrentlyAtDashboardPro ? (
              <HandleSelectDPUserButton
                userId={row.original._id || ""}
                userName={row.original.login.replace("𒐫", "") || "N/A"}
                userRole={Roles.Worker}
              />
            ) : (
              <>
                <Button
                  variant="link"
                  className="text-blue-500"
                  onClick={handleNavigate}
                >
                  {row.original.login?.replace("𒐫", "") || "N/A"}
                </Button>
                {row.original.tgId === null && (
                  <UnnasignedTgTooltip offset={-0.5} />
                )}
              </>
            )}
          </div>
        </DataTableCenteredCell>
      );
    },
  },
  {
    accessorKey: "balance",
    header: () => (
      <DataTableCenteredCell>
        <SortButton sort="usdtBalance.balance" title="Баланс" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {numberWithSpaces(getUSDTBalance(row.original.balances)[0])}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "totalTurnover",
    header: () => (
      <DataTableCenteredCell>
        <SortButton sort="totalTurnover" title="Оборот" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.totalTurnover}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "totalLeadsCount",
    header: () => (
      <DataTableCenteredCell>
        <SortButton sort="totalLeadsCount" title="Лидов" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.totalLeadsCount}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "closedLeadsCount",
    header: () => (
      <DataTableCenteredCell>
        <SortButton sort="closedLeadsCount" title="Закрытий" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.closedLeadsCount}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "mentor",
    header: () => {
      const [isFree, setIsFree] = useQueryState("isFree");

      return (
        <DataTableCenteredCell className="flex-row gap-0">
          <Button
            variant="ghost"
            className="text-thColor max-md:px-0"
            onClick={() => {
              if (isFree === "true" || !isFree) {
                setIsFree("false");
              } else {
                setIsFree("true");
              }
            }}
          >
            <p>Наставник</p>
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>

          {isFree && (
            <button onClick={() => setIsFree(null)}>
              <X size={16} />
            </button>
          )}
        </DataTableCenteredCell>
      );
    },
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.mentor?.userId?.username || "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "lastLeadEnterAt",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        Последний лид
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.lastLeadEnterAt ? (
          <p className="text-textColor">
            {formatDate(row.original.lastLeadEnterAt)}
          </p>
        ) : (
          "N/A"
        )}
      </DataTableCenteredCell>
    ),
  },
];
